import * as client from "../../../client";
import { CatResultsResponse } from "../../../client/types.gen";
import { compareLayers, readableLayer } from "../layers/util";
import {
  LayerPerilRow,
  RmsLayerResult,
  SelectedModel,
  getPerilDisplayName,
} from "./types";

// A completed run's results, keyed by the batch that produced them. Null
// results are a run still loading (or one with nothing modeled); its cells
// render empty rather than dropping the column.
export interface RunResults {
  batchId: string;
  results: CatResultsResponse | null;
}

const LAYER_TOTAL_PERIL = "Combined";

/** The modeled layers one vendor's slice of a results response holds. */
export const vendorLayers = (
  model: SelectedModel,
  results: CatResultsResponse | null
): RmsLayerResult[] => {
  const layers = results?.[model]?.aggregate?.layers;
  if (!layers) return [];
  return layers
    .filter((layer) => layer.layer_guid && layer.layer_guid !== "")
    .map((layer) => ({ ...layer, layer_id: layer.layer_guid }));
};

const compareByResolvedLayer =
  (layerInfoById: Map<string, client.Layer>) =>
  (a: string, b: string): number => {
    const infoA = layerInfoById.get(a);
    const infoB = layerInfoById.get(b);
    if (!infoA) return infoB ? 1 : 0;
    if (!infoB) return -1;
    return compareLayers(infoA, infoB);
  };

const layerAal = (
  layer: RmsLayerResult | undefined,
  peril: string
): number | null => {
  if (!layer) return null;
  if (peril === LAYER_TOTAL_PERIL) return layer.totals?.aal ?? null;
  return layer.perils?.[peril]?.aal ?? null;
};

interface BuildRowsArgs {
  model: SelectedModel;
  // The run whose AAL, PML and PMI fill the row's own fields.
  primary: CatResultsResponse | null;
  // Further runs whose AAL lands in ``aalByBatch``. Layers and perils only
  // they modeled still get rows, with the primary's fields empty.
  comparisons: RunResults[];
  layerInfoById: Map<string, client.Layer>;
  isLayerVisible: (layerId: string) => boolean;
  pmi: client.CatPmiData | null;
}

/**
 * One row per (layer, peril) plus a Layer Total row per layer, in layer order
 * then peril display-name order. Perils and layers are the union across the
 * primary and comparison runs so a comparison can show what one run modeled
 * and another did not.
 */
export const buildLayerPerilRows = ({
  model,
  primary,
  comparisons,
  layerInfoById,
  isLayerVisible,
  pmi,
}: BuildRowsArgs): LayerPerilRow[] => {
  const runs: { batchId: string | null; layers: RmsLayerResult[] }[] = [
    { batchId: null, layers: vendorLayers(model, primary) },
    ...comparisons.map((run) => ({
      batchId: run.batchId,
      layers: vendorLayers(model, run.results),
    })),
  ];
  if (runs.every((run) => run.layers.length === 0)) return [];

  // Layer ids in first-seen order, then sorted by the report's layer config.
  const layerIds: string[] = [];
  const seenLayers = new Set<string>();
  for (const run of runs) {
    for (const layer of run.layers) {
      const id = layer.layer_id;
      if (id && !seenLayers.has(id) && isLayerVisible(id)) {
        seenLayers.add(id);
        layerIds.push(id);
      }
    }
  }
  layerIds.sort(compareByResolvedLayer(layerInfoById));

  const rows: LayerPerilRow[] = [];
  for (const layerId of layerIds) {
    const layerInfo = layerInfoById.get(layerId);
    const layerName = layerInfo ? readableLayer(layerInfo) : layerId;
    const layerByRun = runs.map((run) =>
      run.layers.find((layer) => layer.layer_id === layerId)
    );
    const primaryLayer = layerByRun[0];

    const perils = new Set<string>();
    for (const layer of layerByRun) {
      for (const peril of Object.keys(layer?.perils ?? {})) perils.add(peril);
    }
    const perilCodes = [...perils].sort((a, b) =>
      getPerilDisplayName(a).localeCompare(getPerilDisplayName(b))
    );

    const grAepCurve = (primaryLayer?.ep_curves ?? []).find(
      (c: client.EpRow) => c.perspective === "GR" && c.metric_type === "AEP"
    );

    let isFirst = true;
    for (const peril of [...perilCodes, LAYER_TOTAL_PERIL]) {
      const isTotal = peril === LAYER_TOTAL_PERIL;
      const perilData = isTotal ? undefined : primaryLayer?.perils?.[peril];
      const pmlFor = (period: string): number | null =>
        isTotal
          ? (grAepCurve?.return_period_values?.[period] ?? null)
          : (perilData?.pml_per_period?.[period] ?? null);
      // No all-peril PMI (undocumented cross-peril year alignment).
      const perilPmi = isTotal ? undefined : pmi?.per_peril?.[peril];
      const aalByBatch: Record<string, number | null> = {};
      runs.forEach((run, index) => {
        if (run.batchId !== null) {
          aalByBatch[run.batchId] = layerAal(layerByRun[index], peril);
        }
      });
      rows.push({
        layer: layerName,
        peril,
        aal: layerAal(primaryLayer, peril),
        pml_100: pmlFor("100"),
        pml_250: pmlFor("250"),
        pml_1000: pmlFor("1000"),
        pmi_100: perilPmi?.["100"] ?? null,
        pmi_250: perilPmi?.["250"] ?? null,
        pmi_1000: perilPmi?.["1000"] ?? null,
        aalByBatch,
        isFirstInGroup: isFirst,
      });
      isFirst = false;
    }
  }
  return rows;
};
